import { FC } from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Grid, Paper, Stack, Table, TableBody, Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { AuthProvider, UserDetail, UserRole } from '../../../types/user';
import { countryFlag, fmtDate, PROVIDER_LABELS, ROLE_LABELS } from '../userShowUtils';
import { DeviceIcon, Row } from '../userShowComponents';

/**
 * ⑤ 관리 (접이식 Accordion, 기본 접힘)
 * - 프로필/환경 상세: raw 정보(Firebase UID/timezone/device/모국어/출생연도/가입·로그인)
 * - 관리 액션: 역할 변경 · 구독 변경 · 크레딧 조정 · 깃털 조정 · 비밀번호 변경
 */
const ManagementPanel: FC<{
    user: UserDetail;
    onEditRole: () => void;
    onChangePassword: () => void;
    onEditSubscription: () => void;
    onAdjustCredits: () => void;
    onAdjustFeathers: () => void;
}> = ({ user, onEditRole, onChangePassword, onEditSubscription, onAdjustCredits, onAdjustFeathers }) => {
    const flag = countryFlag(user.countryCode);

    return (
        <Accordion sx={{ mt: 2 }} defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">관리</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
                            <Typography variant="subtitle1" gutterBottom>계정 정보</Typography>
                            <Table size="small">
                                <TableBody>
                                    <Row label="ID" value={user.id} />
                                    <Row label="이메일" value={user.email} />
                                    <Row label="닉네임" value={user.nickname || '-'} />
                                    <Row
                                        label="가입방식"
                                        value={<Chip label={PROVIDER_LABELS[user.provider]} size="small" color="primary" variant="outlined" />}
                                    />
                                    <Row
                                        label="역할"
                                        value={<Chip label={ROLE_LABELS[user.role]} size="small" color={user.role === UserRole.USER ? 'default' : 'warning'} />}
                                    />
                                    <Row label="모국어" value={user.nativeLanguageName || user.nativeLanguageCode} />
                                    {user.birthYear && <Row label="출생연도" value={user.birthYear} />}
                                </TableBody>
                            </Table>
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
                            <Typography variant="subtitle1" gutterBottom>환경 / 가입</Typography>
                            <Table size="small">
                                <TableBody>
                                    <Row
                                        label="디바이스"
                                        value={
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <DeviceIcon deviceType={user.deviceType} />
                                                <Typography variant="body2">{user.deviceType ?? '-'}</Typography>
                                            </Box>
                                        }
                                    />
                                    <Row
                                        label="국가"
                                        value={
                                            <Box display="flex" alignItems="center" gap={1}>
                                                {flag && <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>{flag}</Box>}
                                                <Typography variant="body2">{user.countryCode || '-'}</Typography>
                                            </Box>
                                        }
                                    />
                                    <Row label="타임존" value={user.timezone || '-'} />
                                    <Row label="마지막 로그인" value={fmtDate(user.lastLoginAt)} />
                                    <Row label="가입일" value={fmtDate(user.createdAt)} />
                                    <Row label="Firebase UID" value={<Typography variant="caption">{user.firebaseUid}</Typography>} />
                                </TableBody>
                            </Table>
                        </Paper>
                    </Grid>
                </Grid>

                <Paper variant="outlined" sx={{ p: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>관리 액션</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button variant="outlined" onClick={onEditRole}>역할 변경</Button>
                        <Button variant="outlined" onClick={onEditSubscription}>구독 변경</Button>
                        <Button variant="outlined" onClick={onAdjustCredits}>크레딧 조정</Button>
                        <Button variant="outlined" color="warning" onClick={onAdjustFeathers}>깃털 조정</Button>
                        {user.provider === AuthProvider.EMAIL && !user.deletedAt && (
                            <Button variant="outlined" onClick={onChangePassword}>비밀번호 변경</Button>
                        )}
                    </Stack>
                </Paper>
            </AccordionDetails>
        </Accordion>
    );
};

export default ManagementPanel;
